import React, {useCallback, useEffect, useRef} from 'react';
import {
  AccessibilityInfo,
  findNodeHandle,
  InteractionManager,
  Platform,
  ScrollView,
  useWindowDimensions,
  View,
} from 'react-native';
import Modal from 'react-native-modal';
import {noop} from 'lodash';

import KeyboardFocusable from '/component/base/ExtKeyboard/KeyboardFocusable';
import Icon from '/component/base/Icon';
import RadioButtonGroup from '/component/base/RadioButton/RadioButtonGroup';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import {Reports} from '/constant/TestIDs';
import {useTranslation} from '/provider/TranslationProvider';
import testProperties from '/util/testing/testProperties';

import {styles} from './AllOrdersReport.style';
import reportPayload from './reportPayload';

import {useRecordsSorting} from '/context/RecordsSort/useRecordsSorting';

const MAX_FONT_SIZE_MULTIPLIER = 2;

export const ModalFilter = ({
  setModalVisible = noop,
  modalVisible = false,
  setApplyFilter = noop,
  applyFilter = false,
  filterCount = 0,
  generationOptions = [],
  setGenerationOptions = noop,
  generationWithoutFilter = {},
  productsOptions = [],
  setProductsOptions = noop,
  productsWithoutFilter = {},
  accountStatusOptions = [],
  setAccountStatusOptions = noop,
  accountWithoutFilter = {},
  setGenerationWithoutFilter = noop,
  setProductsWithoutFilter = noop,
  setAccountWithoutFilter = noop,
}: any) => {
  const {tt} = useTranslation('report.allOrders');

  const {height} = useWindowDimensions();

  const modalHeight = height > 684 ? height * 0.6 : height * 0.9;

  const {isReadyToLoad} = useRecordsSorting({
    recordsName: reportPayload.reportName,
  });

  const closeButtonFocusRef = useRef<any>(null);
  const closeButtonComponentRef = useRef<any>(null);
  const hasFocusedCloseButtonRef = useRef(false);

  const handleCloseButtonFocus = useCallback(() => {
    hasFocusedCloseButtonRef.current = true;
  }, []);

  const focusCloseButton = useCallback(() => {
    if (!modalVisible || hasFocusedCloseButtonRef.current) {
      return;
    }

    const canFocus = typeof closeButtonFocusRef.current?.focus === 'function';

    const handle = closeButtonComponentRef.current
      ? findNodeHandle(closeButtonComponentRef.current)
      : null;

    if (!canFocus && !handle) {
      return;
    }

    if (canFocus) {
      closeButtonFocusRef.current.focus();
    }

    if (
      handle &&
      typeof AccessibilityInfo.setAccessibilityFocus === 'function'
    ) {
      AccessibilityInfo.setAccessibilityFocus(handle);
    }
  }, [modalVisible]);

  useEffect(() => {
    if (!modalVisible) {
      hasFocusedCloseButtonRef.current = false;
      return;
    }

    const interactionHandle = InteractionManager.runAfterInteractions(() => {
      requestAnimationFrame(focusCloseButton);
    });

    const timeoutId = setTimeout(focusCloseButton, 500);

    return () => {
      interactionHandle?.cancel?.();
      clearTimeout(timeoutId);
    };
  }, [modalVisible, focusCloseButton]);

  const applyFilterFunc = () => {
    if (!applyFilter) {
      setGenerationOptions(
        generationOptions?.map((f: any) => ({
          ...f,
          selected: f.id === generationWithoutFilter?.id,
        })),
      );

      setProductsOptions(
        productsOptions?.map((f: any) => ({
          ...f,
          selected: f.id === productsWithoutFilter?.id,
        })),
      );

      setAccountStatusOptions(
        accountStatusOptions?.map((f: any) => ({
          ...f,
          selected: f.id === accountWithoutFilter?.id,
        })),
      );
    }
  };

  const closeModal = () => {
    setModalVisible(false);
    applyFilterFunc();
  };

  const handleApplyFiltersPress = () => {
    setModalVisible(false);
    setApplyFilter(true);

    const generationFilterTemp = generationOptions?.find(
      (item: any) => item.selected,
    );

    const productsOptionsFilter = productsOptions?.find(
      (item: any) => item.selected,
    );

    const accountStatusOptionsFilter = accountStatusOptions?.find(
      (item: any) => item.selected,
    );

    setGenerationWithoutFilter(generationFilterTemp);
    setProductsWithoutFilter(productsOptionsFilter);
    setAccountWithoutFilter(accountStatusOptionsFilter);
  };

  if (!isReadyToLoad) {
    return null;
  }

  return (
    <Modal
      isVisible={modalVisible}
      style={styles.modal}
      onBackdropPress={closeModal}
      onModalShow={focusCloseButton}
      useNativeDriver={false}
      useNativeDriverForBackdrop={false}
      hideModalContentWhileAnimating={false}
      animationIn={Platform.OS === 'web' ? 'slideInRight' : 'slideInUp'}
      animationOut={Platform.OS === 'web' ? 'slideOutRight' : 'slideOutDown'}
      avoidKeyboard>
      <View style={[styles.modalView, {height: modalHeight}]}>
        <View style={styles.modalHeader} accessible={false}>
          <KeyboardFocusable
            ref={closeButtonFocusRef}
            componentRef={closeButtonComponentRef}
            accessible
            accessibilityRole="button"
            accessibilityLabel="Close"
            importantForAccessibility="yes"
            autoFocus={modalVisible}
            onLayout={focusCloseButton}
            onFocus={handleCloseButtonFocus}
            onPress={closeModal}
            {...testProperties(Reports.reportFiltersModalClose, true)}>
            <Icon name="close" />
          </KeyboardFocusable>

          <Text
            accessible
            accessibilityRole="header"
            importantForAccessibility="yes"
            style={styles.headerText}
            allowFontScaling
            maxFontSizeMultiplier={MAX_FONT_SIZE_MULTIPLIER}
            numberOfLines={1}
            ellipsizeMode="tail"
            {...testProperties(Reports.reportFiltersModalHeader)}
            isBold>
            {tt('filters', 'Filters')}
          </Text>

          <TouchableOpacity
            accessible
            accessibilityRole="button"
            accessibilityLabel={`Apply filters (${filterCount})`}
            importantForAccessibility="yes"
            onPress={handleApplyFiltersPress}
            style={styles.applyFiltersContainer}
            {...testProperties(Reports.reportFiltersModalApplyButton)}>
            <Text
              isBold
              style={styles.applyFiltersText}
              allowFontScaling
              maxFontSizeMultiplier={MAX_FONT_SIZE_MULTIPLIER}>
              {tt('Applyfilters', `Apply filters (${filterCount})`)}
            </Text>
          </TouchableOpacity>
        </View>

        <ScrollView
          style={styles.modalScroll}
          contentContainerStyle={styles.radioButtonsContainer}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}>
          <RadioButtonGroup title="Generation" options={generationOptions} />

          <RadioButtonGroup title="Product Type" options={productsOptions} />

          <RadioButtonGroup
            title="Account status"
            options={accountStatusOptions}
          />
        </ScrollView>
      </View>
    </Modal>
  );
};
